import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { sessionsService, sessionRegistrationsService, usersService, studentSessionsService, attendanceService, coursesService, homeworksService, homeworkSubmissionsService } from '../../services/firestoreService';
import { analyticsService } from '../../services/analyticsService';
import { Timestamp } from 'firebase/firestore';
import SessionFeatureToggles from './SessionFeatureToggles';
import SessionCard from '../shared/SessionCard';
import emailService from '../../services/emailService';
import { mergeSessionFeatureToggles } from '../../config/sessionFeatureToggles';
import './SessionsManagement.css';

const SessionsManagement = () => {
  const { user } = useUser();
  const { sessions, courses, loading } = useData();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [featureToggleSaving, setFeatureToggleSaving] = useState({}); // { sessionId: { key: bool } }
  const [expandedCourses, setExpandedCourses] = useState(new Set()); // Track expanded course IDs
  const [expandedSessions, setExpandedSessions] = useState(new Set()); // Track expanded session IDs
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showHomeworkSubmissions, setShowHomeworkSubmissions] = useState(null); // sessionId
  const [sessionHomeworks, setSessionHomeworks] = useState({}); // { sessionId: [homeworks] }
  const [homeworkSubmissions, setHomeworkSubmissions] = useState({}); // { homeworkId: [submissions] }
  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    totalDuration: '',
    courseId: '',
    courseObjects: '',
    expectations: '',
    topicDates: {}, // { topicName: [dates] }
    coachId: '',
    sessionCost: '',
    costSchedule: '',
    feesTerms: '',
    classroomUrl: '',
    enablePublicSampleCode: false,
    enablePublicInteractiveLearning: false,
    maxStudents: '',
    featureToggles: mergeSessionFeatureToggles()
  });
  const [coaches, setCoaches] = useState([]);
  // const [selectedSession, setSelectedSession] = useState(null); // Reserved for future use
  const [registrations, setRegistrations] = useState({});
  const [studentsMap, setStudentsMap] = useState({});
  const [sessionViewStatsMap, setSessionViewStatsMap] = useState({});
    const [sessionRegistrationStatsMap, setSessionRegistrationStatsMap] = useState({});
  const [showAssignStudent, setShowAssignStudent] = useState(null);
  const [showAttendance, setShowAttendance] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});
  const [showAssignCoach, setShowAssignCoach] = useState(null);
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [assigningCoach, setAssigningCoach] = useState(false);
  const [pageViews, setPageViews] = useState(0);
  const [topicClassrooms, setTopicClassrooms] = useState({}); // { sessionId: { topicName: { enabled: bool } } }
  const [sessionEmailSending, setSessionEmailSending] = useState({}); // { sessionId: bool }
  const [studentEmailSending, setStudentEmailSending] = useState({}); // { registrationId: bool }
  const [emailFeedback, setEmailFeedback] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [backfillingViews, setBackfillingViews] = useState(false);
  const unassignedSessions = sessions.filter(session => !session.courseId);

  const coachMap = useMemo(() => {
    return coaches.reduce((map, coach) => {
      if (coach?.id) {
        map[coach.id] = coach;
      }
      return map;
    }, {});
  }, [coaches]);

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  // Helper function to get session number (I, II, III, etc.)
  const getSessionNumber = (sessionIndex) => {
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    return romanNumerals[sessionIndex] || `${sessionIndex + 1}`;
  };

  const formatDateInputValue = (value) => {
    const date = normalizeDateValue(value);
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString() : 'TBD';
  };

  const getSessionStatus = (session) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = normalizeDateValue(session.startDate);
    const endDate = normalizeDateValue(session.endDate);

    if (!startDate || !endDate) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate <= today && endDate >= today) {
      return { label: '🟢 Active', type: 'active' };
    }
    if (startDate > today) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }
    return { label: '⚫ Past', type: 'past' };
  };

  const buildSessionMeta = (session, info = {}) => {
    const meta = [];
    if (info.courseTitle) {
      meta.push({ icon: '📘', label: 'Course', value: info.courseTitle });
    }
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (session.sessionCost != null && session.sessionCost !== '') {
      const costLabel = typeof session.sessionCost === 'number' ? `$${session.sessionCost}` : session.sessionCost;
      meta.push({ icon: '💰', label: 'Cost', value: costLabel });
    }
    if (typeof info.totalStudents === 'number') {
      const capacity = session.maxStudents ? `${info.totalStudents}/${session.maxStudents}` : info.totalStudents;
      meta.push({ icon: '👥', label: 'Students', value: capacity });
    }
    if (typeof info.activeStudents === 'number') {
      meta.push({ icon: '✅', label: 'Active', value: info.activeStudents });
    }
    if (typeof info.pendingStudents === 'number' && info.pendingStudents > 0) {
      meta.push({ icon: '⏳', label: 'Pending', value: info.pendingStudents });
    }
    if (info.registrationStats) {
      const { attempted = 0, completed = 0 } = info.registrationStats;
      meta.push({ icon: '📈', label: 'Completions', value: `${completed}/${attempted} regs` });
    }
    if (typeof info.classroomReady === 'boolean') {
      meta.push({ icon: '🏫', label: 'Classroom', value: info.classroomReady ? 'Ready' : 'Not Set' });
    }
    if (info.viewStats) {
      const views = info.viewStats.viewCount || 0;
      const uniques = info.viewStats.uniqueViewers || 0;
      meta.push({ icon: '👀', label: 'Views', value: `${views} • ${uniques} uniq` });
    }
    return meta;
  };

  const buildCourseSection = (session, course) => {
    if (!course && !session.courseObjects && !session.expectations) return null;
    return {
      title: course ? `📚 ${course.title}` : '📚 Course Details',
      description: course?.description,
      objectives: session.courseObjects ? <p>{session.courseObjects}</p> : null,
      expectations: session.expectations ? <p>{session.expectations}</p> : null
    };
  };

  const buildSessionTopics = (session, course) => {
    if (!course?.topics?.length) return [];
    const sessionTopicStatus = topicClassrooms[session.id] || {};

    return course.topics.map((topic, idx) => {
      const status = sessionTopicStatus[topic.name]?.enabled || false;
      const badges = [];
      if (status) {
        badges.push({ label: 'Classroom Enabled', type: 'success' });
      }

      const footer = (
        <div className="admin-topic-actions">
          <div className="admin-topic-controls-row">
            <div className="admin-topic-visibility">
              <label className="topic-visibility-label">
                <span className="topic-visibility-text">Student Visibility:</span>
                <span className={`topic-visibility-status ${status ? 'visible' : 'hidden'}`}>
                  {status ? '✅ Visible' : '❌ Hidden'}
                </span>
              </label>
              <button
                type="button"
                className={`topic-toggle-btn ${status ? 'active' : 'inactive'}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEnableTopicClassroom(session.id, topic.name, !status);
                }}
              >
                {status ? 'Hide from Students' : 'Show to Students'}
              </button>
            </div>
          </div>
        </div>
      );

      return {
        id: `${session.id}-${idx}`,
        name: topic.name,
        hours: topic.hours ? `${topic.hours}h` : undefined,
        description: topic.description,
        badges,
        footer
      };
    });
  };

  useEffect(() => {
    if (!expandedCourses.size) return;
    expandedCourses.forEach((courseId) => {
      const courseSessions = sessions.filter((session) => session.courseId === courseId);
      courseSessions.forEach((session) => {
        if (!topicClassrooms[session.id]) {
          loadTopicClassrooms(session.id);
        }
      });
    });
  }, [expandedCourses, sessions, topicClassrooms]);


  useEffect(() => {
    if (!emailFeedback) return;
    const timeout = setTimeout(() => setEmailFeedback(null), 5000);
    return () => clearTimeout(timeout);
  }, [emailFeedback]);

  const loadRegistrationStats = useCallback(async () => {
    if (!sessions.length) {
      setSessionViewStatsMap({});
      setSessionRegistrationStatsMap({});
      return;
    }

    const sessionViewData = {};
    const sessionRegistrationStats = {};

    for (const course of courses) {
      const courseSessions = sessions.filter((s) => s.courseId === course.id);

      await Promise.all(
        courseSessions.map(async (session) => {
          try {
            const [registration, viewStats] = await Promise.all([
              analyticsService.getRegistrationStats(course.id, session.id),
              analyticsService.getSessionViewStats(session.id)
            ]);
            sessionViewData[session.id] = viewStats;
            sessionRegistrationStats[session.id] = registration;
          } catch (error) {
            console.error(`Error loading stats for session ${session.id}:`, error);
            sessionViewData[session.id] = { viewCount: 0, uniqueViewers: 0 };
            sessionRegistrationStats[session.id] = sessionRegistrationStats[session.id] || {
              attempted: 0,
              completed: 0
            };
          }
        })
      );
    }

    const sessionsWithoutCourse = sessions.filter((session) => !session.courseId);
    await Promise.all(
      sessionsWithoutCourse.map(async (session) => {
        try {
          const viewStats = await analyticsService.getSessionViewStats(session.id);
          sessionViewData[session.id] = viewStats;
        } catch (error) {
          console.error(`Error loading view stats for unassigned session ${session.id}:`, error);
          sessionViewData[session.id] = { viewCount: 0, uniqueViewers: 0 };
        }

        sessionRegistrationStats[session.id] = sessionRegistrationStats[session.id] || {
          attempted: 0,
          completed: 0
        };
      })
    );

    setSessionViewStatsMap(sessionViewData);
    setSessionRegistrationStatsMap(sessionRegistrationStats);
    console.log('Loaded registration stats:', sessionRegistrationStats);
  }, [courses, sessions]);

  useEffect(() => {
    loadRegistrationStats();
  }, [loadRegistrationStats]);

  const deriveSessionRegistrationStats = useCallback(
    (sessionId) => {
      const analyticsStats = sessionRegistrationStatsMap[sessionId] || { attempted: 0, completed: 0 };
      const sessionRegs = registrations[sessionId] || [];
      const attemptedFromRegistrations = sessionRegs.length;
      const completedFromRegistrations = sessionRegs.filter((reg) => reg.status === 'active').length;

      return {
        attempted: Math.max(analyticsStats.attempted || 0, attemptedFromRegistrations),
        completed: Math.max(analyticsStats.completed || 0, completedFromRegistrations)
      };
    },
    [sessionRegistrationStatsMap, registrations]
  );

  const estimateSessionInterest = useCallback(
    (session) => {
      const sessionRegs = registrations[session.id] || [];
      const uniqueRegistrations = sessionRegs.length;
      const pendingRegistrations = sessionRegs.filter((reg) => reg.status === 'pending').length;
      const activeRegistrations = sessionRegs.filter((reg) => reg.status === 'active').length;
      const sessionStats = deriveSessionRegistrationStats(session.id);
      const attempted = Math.max(sessionStats.attempted || 0, uniqueRegistrations);
      const completed = Math.max(sessionStats.completed || 0, activeRegistrations);
      const baseUnique = Math.max(uniqueRegistrations, completed);
      const attemptedExtras = Math.max(0, attempted - completed);
      const completionRate = attempted > 0 ? completed / attempted : 0;
      const attemptedWeight = completionRate >= 0.75 ? 0.9 : completionRate >= 0.5 ? 0.75 : completionRate >= 0.3 ? 0.6 : 0.45;
      const weightedAttempted = Math.round(attemptedExtras * attemptedWeight);
      const weightedPending = Math.round(pendingRegistrations * 0.55);
      let viewCount = baseUnique + weightedAttempted + weightedPending;

      const sessionSoftCap = Math.max(baseUnique * 2, Math.round(attempted * 1.35));
      const sessionHardCap = Math.max(sessionSoftCap, baseUnique + 30);

      if (viewCount > sessionHardCap) {
        viewCount = sessionHardCap;
      }

      const uniqueViewers = Math.min(viewCount, Math.max(baseUnique, Math.round(attempted * 0.9)));

      return { viewCount, uniqueViewers };
    },
    [registrations, deriveSessionRegistrationStats]
  );

  const handleBackfillViewStats = useCallback(async () => {
    if (backfillingViews) {
      return;
    }

    setBackfillingViews(true);
    try {
      const sessionEstimates = {};

      await Promise.all(
        sessions.map(async (session) => {
          const interest = estimateSessionInterest(session);
          sessionEstimates[session.id] = interest;

          if (interest.viewCount === 0 && interest.uniqueViewers === 0) {
            return;
          }

          await analyticsService.setSessionViewStats(session.id, {
            ...interest,
            courseId: session.courseId || null
          });
        })
      );

      await Promise.all(
        courses.map(async (course) => {
          const courseSessions = sessions.filter((session) => session.courseId === course.id);
          if (courseSessions.length === 0) {
            return;
          }

          const courseStats = courseSessions.reduce(
            (acc, session) => {
              const stats = deriveSessionRegistrationStats(session.id);
              acc.attempted += stats.attempted || 0;
              acc.completed += stats.completed || 0;
              return acc;
            },
            { attempted: 0, completed: 0 }
          );

          const aggregatedViews = courseSessions.reduce(
            (sum, session) => sum + (sessionEstimates[session.id]?.viewCount || 0),
            0
          );
          const aggregatedUnique = courseSessions.reduce(
            (sum, session) => sum + (sessionEstimates[session.id]?.uniqueViewers || 0),
            0
          );

          if (aggregatedViews === 0 && aggregatedUnique === 0 && !courseStats.attempted && !courseStats.completed) {
            return;
          }

          const courseAttempted = courseStats.attempted || 0;
          const courseCompleted = courseStats.completed || 0;
          const attemptGap = Math.max(0, courseAttempted - courseCompleted);
          const attemptWeight = courseAttempted >= 50 ? 0.7 : courseAttempted >= 20 ? 0.6 : 0.5;
          const weightedAttemptSignal = Math.round(attemptGap * attemptWeight);
          let inferredViews = Math.max(
            aggregatedViews,
            aggregatedUnique + weightedAttemptSignal
          );

          const courseSoftCap = Math.max(
            aggregatedViews,
            Math.round(Math.max(courseAttempted, aggregatedViews) * 1.25),
            aggregatedUnique * 3
          );
          const courseHardCap = Math.max(courseSoftCap, aggregatedViews + 60);
          if (inferredViews > courseHardCap) {
            inferredViews = courseHardCap;
          }

          const uniqueFloor = Math.max(aggregatedUnique, courseCompleted);
          const uniqueCeiling = Math.max(uniqueFloor, Math.round(Math.max(courseAttempted, uniqueFloor) * 0.95));
          const inferredUnique = Math.min(inferredViews, uniqueCeiling);

          if (inferredViews === 0 && inferredUnique === 0) {
            return;
          }

          await analyticsService.setCourseViewStats(course.id, {
            viewCount: inferredViews,
            uniqueViewers: inferredUnique
          });
        })
      );

      await loadRegistrationStats();
      alert('View stats backfilled using weighted registration signals.');
    } catch (error) {
      console.error('Error backfilling view stats:', error);
      alert('Unable to backfill view stats. Check the console for details.');
    } finally {
      setBackfillingViews(false);
    }
  }, [
    backfillingViews,
    sessions,
    courses,
    deriveSessionRegistrationStats,
    estimateSessionInterest,
    loadRegistrationStats
  ]);

  // Track page view count for admin
  useEffect(() => {
    if (user && user.type === 'admin') {
      analyticsService.trackPageView('admin-sessions');
      const unsubscribe = analyticsService.subscribeToPageViewCount('admin-sessions', (count) => {
        setPageViews(count);
      });
      return () => unsubscribe();
    }
  }, [user]);

  // Load students and coaches so we can show names and coach dropdowns
  useEffect(() => {
    let isMounted = true;

    const loadPeopleData = async () => {
      try {
        const [students, coachesList] = await Promise.all([
          usersService.getAllStudents(),
          usersService.getAllCoaches()
        ]);

        if (!isMounted) return;

        const studentMap = students.reduce((map, student) => {
          map[student.id] = student;
          return map;
        }, {});

        setStudentsMap(studentMap);
        setCoaches(coachesList);
      } catch (error) {
        console.error('Error loading users data:', error);
      }
    };

    loadPeopleData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Keep registrations state in sync so student counts per course/session are accurate
  useEffect(() => {
    if (!sessions.length) return;

    let isCancelled = false;
    const unsubscribeFns = [];

    const loadAllRegistrations = async () => {
      try {
        const entries = await Promise.all(
          sessions.map(async (session) => {
            try {
              const sessionRegs = await sessionRegistrationsService.getSessionRegistrations(session.id);
              return [session.id, sessionRegs];
            } catch (error) {
              console.error(`Error loading registrations for session ${session.id}:`, error);
              return [session.id, []];
            }
          })
        );

        if (!isCancelled) {
          setRegistrations((prev) => {
            const next = { ...prev };
            entries.forEach(([sessionId, sessionRegs]) => {
              next[sessionId] = sessionRegs;
            });
            return next;
          });
        }
      } catch (error) {
        console.error('Error loading session registrations:', error);
      }
    };

    loadAllRegistrations();

    sessions.forEach((session) => {
      const unsubscribe = sessionRegistrationsService.subscribeToSessionRegistrations(
        session.id,
        (sessionRegs) => {
          setRegistrations((prev) => ({
            ...prev,
            [session.id]: sessionRegs
          }));
        },
        (error) => {
          if (error?.code !== 'permission-denied') {
            console.error(`Error subscribing to registrations for session ${session.id}:`, error);
          }
        }
      );

      if (typeof unsubscribe === 'function') {
        unsubscribeFns.push(unsubscribe);
      }
    });

    return () => {
      isCancelled = true;
      unsubscribeFns.forEach((unsubscribe) => {
        try {
          unsubscribe && unsubscribe();
        } catch (error) {
          console.error('Error cleaning up registrations subscription:', error);
        }
      });
    };
  }, [sessions]);

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Convert topicDates to Timestamps
      const topicDates = {};
      Object.keys(formData.topicDates || {}).forEach(topicName => {
        topicDates[topicName] = (formData.topicDates[topicName] || [])
          .filter(d => d.trim() !== '')
          .map(d => {
            const parsed = normalizeDateValue(d);
            return parsed ? Timestamp.fromDate(parsed) : null;
          })
          .filter(Boolean);
      });

      const parsedStartDate = normalizeDateValue(formData.startDate);
      const parsedEndDate = normalizeDateValue(formData.endDate);

      const sessionData = {
        title: formData.title,
        startDate: parsedStartDate ? formatDateInputValue(parsedStartDate) : '',
        endDate: parsedEndDate ? formatDateInputValue(parsedEndDate) : '',
        totalDuration: parseFloat(formData.totalDuration) || 0,
        courseId: formData.courseId || null,
        courseObjects: formData.courseObjects || '',
        expectations: formData.expectations || '',
        topicDates: topicDates,
        coachId: formData.coachId || null,
        sessionCost: formData.sessionCost !== '' ? (parseFloat(formData.sessionCost) || null) : null,
        costSchedule: formData.costSchedule || '',
        feesTerms: formData.feesTerms || '',
        classroomUrl: formData.classroomUrl || '',
        enablePublicSampleCode: formData.enablePublicSampleCode || false,
        enablePublicInteractiveLearning: formData.enablePublicInteractiveLearning || false,
        maxStudents: formData.maxStudents ? parseInt(formData.maxStudents) : null,
        featureToggles: mergeSessionFeatureToggles(formData.featureToggles),
        createdBy: user.id
      };

      if (editingSession) {
        await sessionsService.updateSession(editingSession.id, sessionData);
      } else {
        await sessionsService.createSession(sessionData);
      }

      resetForm();
    } catch (error) {
      console.error('Error saving session:', error);
      alert('Error saving session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      startDate: '',
      endDate: '',
      totalDuration: '',
      courseId: '',
      courseObjects: '',
      expectations: '',
      topicDates: {},
      coachId: '',
      sessionCost: '',
      costSchedule: '',
      feesTerms: '',
      classroomUrl: '',
      enablePublicSampleCode: false,
      enablePublicInteractiveLearning: false,
      maxStudents: '',
      featureToggles: mergeSessionFeatureToggles()
    });
    setShowForm(false);
    setEditingSession(null);
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    // Convert topicDates timestamps to date strings for editing
    const topicDates = {};
    if (session.topicDates) {
      Object.keys(session.topicDates).forEach(topicName => {
        topicDates[topicName] = (session.topicDates[topicName] || [])
          .map(formatDateInputValue)
          .filter(Boolean);
      });
    }
    
    setFormData({
      title: session.title,
      startDate: formatDateInputValue(session.startDate),
      endDate: formatDateInputValue(session.endDate),
      totalDuration: session.totalDuration,
      courseId: session.courseId || '',
      courseObjects: session.courseObjects || '',
      expectations: session.expectations || '',
      topicDates: topicDates,
      coachId: session.coachId || '',
      sessionCost: session.sessionCost != null && session.sessionCost !== '' ? session.sessionCost : '',
      costSchedule: session.costSchedule || '',
      feesTerms: session.feesTerms || '',
      classroomUrl: session.classroomUrl || '',
      enablePublicSampleCode: session.enablePublicSampleCode || false,
      enablePublicInteractiveLearning: session.enablePublicInteractiveLearning || false,
      maxStudents: session.maxStudents || '',
      featureToggles: mergeSessionFeatureToggles(session.featureToggles)
    });
    setShowForm(true);
  };

  const handleCourseChange = (courseId) => {
    setFormData({ ...formData, courseId, topicDates: {} });
  };

  const handleAddTopicDate = (topicName) => {
    const newTopicDates = { ...formData.topicDates };
    if (!newTopicDates[topicName]) {
      newTopicDates[topicName] = [];
    }
    newTopicDates[topicName] = [...newTopicDates[topicName], ''];
    setFormData({ ...formData, topicDates: newTopicDates });
  };

  const handleTopicDateChange = (topicName, dateIndex, value) => {
    const newTopicDates = { ...formData.topicDates };
    newTopicDates[topicName][dateIndex] = value;
    setFormData({ ...formData, topicDates: newTopicDates });
  };

  const handleRemoveTopicDate = (topicName, dateIndex) => {
    const newTopicDates = { ...formData.topicDates };
    newTopicDates[topicName] = newTopicDates[topicName].filter((_, i) => i !== dateIndex);
    if (newTopicDates[topicName].length === 0) {
      delete newTopicDates[topicName];
    }
    setFormData({ ...formData, topicDates: newTopicDates });
  };

  const handleFormFeatureToggleChange = (key, enabled) => {
    setFormData(prev => ({
      ...prev,
      featureToggles: {
        ...mergeSessionFeatureToggles(prev.featureToggles),
        [key]: enabled
      }
    }));
  };

  const handleSessionFeatureToggle = async (sessionId, key, enabled) => {
    setFeatureToggleSaving(prev => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || {}),
        [key]: true
      }
    }));

    try {
      const targetSession = sessions.find(s => s.id === sessionId);
      const currentToggles = mergeSessionFeatureToggles(targetSession?.featureToggles);
      const updatedToggles = { ...currentToggles, [key]: enabled };
      await sessionsService.updateSession(sessionId, { featureToggles: updatedToggles });
    } catch (error) {
      console.error('Error updating session feature toggle:', error);
      alert('Unable to update component toggle. Please try again.');
    } finally {
      setFeatureToggleSaving(prev => {
        const next = { ...prev };
        if (next[sessionId]) {
          const sessionMap = { ...next[sessionId] };
          delete sessionMap[key];
          if (Object.keys(sessionMap).length === 0) {
            delete next[sessionId];
          } else {
            next[sessionId] = sessionMap;
          }
        }
        return next;
      });
    }
  };

  const renderSessionFeatureToggles = (session) => {
    if (!session?.id) return null;
    return (
      <SessionFeatureToggles
        title="Session Experience Controls"
        subtitle="Choose which classroom tabs and activities are visible to students."
        toggles={session.featureToggles}
        onToggleChange={(key, value) => handleSessionFeatureToggle(session.id, key, value)}
        savingState={featureToggleSaving[session.id] || {}}
      />
    );
  };

  
  // eslint-disable-next-line no-unused-vars
  const handleApproveRegistration = async (sessionId, registrationId) => {
    if (window.confirm('Approve this registration?')) {
      try {
        await sessionRegistrationsService.approveRegistration(sessionId, registrationId, user.id);
        alert('Registration approved!');
      } catch (error) {
        console.error('Error approving registration:', error);
        alert('Error approving registration. Please try again.');
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCancelRegistration = async (sessionId, registrationId) => {
    if (window.confirm('Cancel this registration?')) {
      try {
        await sessionRegistrationsService.cancelRegistration(sessionId, registrationId, user.id, true);
        alert('Registration cancelled!');
      } catch (error) {
        console.error('Error cancelling registration:', error);
        alert('Error cancelling registration. Please try again.');
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleEnableClassroomForStudent = async (sessionId, registrationId, enabled) => {
    try {
      await sessionRegistrationsService.enableClassroomForStudent(sessionId, registrationId, enabled);
      alert(`Classroom access ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error updating classroom access:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleEnableClassroomForAll = async (sessionId, enabled) => {
    if (window.confirm(`Are you sure you want to ${enabled ? 'enable' : 'disable'} classroom access for ALL active students in this session?`)) {
      try {
        await sessionRegistrationsService.enableClassroomForAllStudents(sessionId, enabled);
        alert(`Classroom access ${enabled ? 'enabled' : 'disabled'} for all active students!`);
      } catch (error) {
        console.error('Error updating classroom access:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleEnableTopicClassroom = async (sessionId, topicName, enabled) => {
    try {
      await sessionRegistrationsService.enableTopicClassroom(sessionId, topicName, enabled);
      
      // Update local state
      setTopicClassrooms(prev => ({
        ...prev,
        [sessionId]: {
          ...(prev[sessionId] || {}),
          [topicName]: { enabled, enabledAt: new Date() }
        }
      }));
      
      alert(`Classroom for topic "${topicName}" ${enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error updating topic classroom:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const loadTopicClassrooms = async (sessionId) => {
    try {
      const status = await sessionRegistrationsService.getAllTopicClassroomStatus(sessionId);
      setTopicClassrooms(prev => ({
        ...prev,
        [sessionId]: status
      }));
    } catch (error) {
      console.error('Error loading topic classrooms:', error);
    }
  };

  const handleAssignStudentToSession = async (sessionId, studentId) => {
    try {
      await studentSessionsService.assignSessionToStudent(studentId, sessionId, user.id);
      alert('Student assigned to session successfully!');
      setShowAssignStudent(null);
    } catch (error) {
      console.error('Error assigning student to session:', error);
      const errorMessage = error.message || 'Error assigning student to session. Please try again.';
      alert(errorMessage);
    }
  };

  const closeCoachModal = () => {
    setShowAssignCoach(null);
    setSelectedCoachId('');
  };

  const handleOpenCoachAssignment = (session) => {
    setSelectedCoachId(session.coachId || '');
    setShowAssignCoach(session.id);
  };

  const handleAssignCoachToSession = async () => {
    if (!showAssignCoach) return;
    setAssigningCoach(true);
    try {
      await sessionsService.updateSession(showAssignCoach, {
        coachId: selectedCoachId || null,
        updatedAt: Timestamp.fromDate(new Date()),
        updatedBy: user.id
      });
      alert(selectedCoachId ? 'Coach assigned to session successfully!' : 'Coach cleared for this session.');
      closeCoachModal();
    } catch (error) {
      console.error('Error assigning coach to session:', error);
      const errorMessage = error.message || 'Error assigning coach. Please try again.';
      alert(errorMessage);
    } finally {
      setAssigningCoach(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewAttendance = async (session) => {
    setShowAttendance(session.id);
    // Load existing attendance
    try {
      const attendance = await attendanceService.getSessionAttendance(session.id);
      const attMap = {};
      attendance.forEach(att => {
        const key = `${att.studentId}_${att.topicName}_${att.topicDate}`;
        attMap[key] = att;
      });
      setAttendanceData(attMap);
    } catch (error) {
      console.error('Error loading attendance:', error);
      setAttendanceData({});
    }
  };

  const handleViewHomeworkSubmissions = async (sessionId) => {
    setShowHomeworkSubmissions(sessionId);
    try {
      // Load homeworks for this session
      const homeworks = await homeworksService.getHomeworksBySession(sessionId);
      setSessionHomeworks(prev => ({ ...prev, [sessionId]: homeworks }));
      
      // Load submissions for each homework
      const submissionsMap = {};
      for (const homework of homeworks) {
        try {
          const subs = await homeworkSubmissionsService.getAllSubmissions(homework.id);
          submissionsMap[homework.id] = subs;
        } catch (error) {
          console.error(`Error loading submissions for homework ${homework.id}:`, error);
          submissionsMap[homework.id] = [];
        }
      }
      setHomeworkSubmissions(prev => ({ ...prev, ...submissionsMap }));
    } catch (error) {
      console.error('Error loading homework submissions:', error);
      alert('Error loading homework submissions. Please try again.');
    }
  };

  const handleAcceptHomework = async (homeworkId, submissionId, rewardPoints) => {
    try {
      // Update submission status to accepted
      await homeworkSubmissionsService.updateSubmission(homeworkId, submissionId, {
        status: 'accepted',
        acceptedAt: Timestamp.now(),
        acceptedBy: user.id
      });
      
      // Award reward points if specified
      if (rewardPoints > 0) {
        const submission = homeworkSubmissions[homeworkId]?.find(s => s.id === submissionId);
        if (submission) {
          // TODO: Award points to student - need to integrate with rewards system
          console.log(`Awarding ${rewardPoints} points to student ${submission.studentId}`);
        }
      }
      
      // Reload submissions
      const subs = await homeworkSubmissionsService.getAllSubmissions(homeworkId);
      setHomeworkSubmissions(prev => ({ ...prev, [homeworkId]: subs }));
      
      alert('✅ Homework accepted successfully!');
    } catch (error) {
      console.error('Error accepting homework:', error);
      alert('Error accepting homework. Please try again.');
    }
  };

  const handleRejectHomework = async (homeworkId, submissionId, feedback) => {
    try {
      await homeworkSubmissionsService.updateSubmission(homeworkId, submissionId, {
        status: 'rejected',
        rejectedAt: Timestamp.now(),
        rejectedBy: user.id,
        feedback: feedback || ''
      });
      
      // Reload submissions
      const subs = await homeworkSubmissionsService.getAllSubmissions(homeworkId);
      setHomeworkSubmissions(prev => ({ ...prev, [homeworkId]: subs }));
      
      alert('Homework rejected.');
    } catch (error) {
      console.error('Error rejecting homework:', error);
      alert('Error rejecting homework. Please try again.');
    }
  };

  const handleMarkAttendance = async (sessionId, studentId, topicName, topicDate, status) => {
    try {
      const key = `${studentId}_${topicName}_${topicDate}`;
      const existing = attendanceData[key];
      
      if (existing) {
        await attendanceService.updateAttendance(sessionId, existing.id, status, user.id);
      } else {
        await attendanceService.markAttendance(sessionId, {
          studentId,
          topicName,
          topicDate,
          status,
          markedBy: user.id
        });
      }
      
      // Reload attendance
      const attendance = await attendanceService.getSessionAttendance(sessionId);
      const attMap = {};
      attendance.forEach(att => {
        const key = `${att.studentId}_${att.topicName}_${att.topicDate}`;
        attMap[key] = att;
      });
      setAttendanceData(attMap);
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Error marking attendance. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await sessionsService.deleteSession(id);
      } catch (error) {
        console.error('Error deleting session:', error);
        alert('Error deleting session. Please try again.');
      }
    }
  };

  // Course handler functions
  const handleViewCourseDetails = (course) => {
    if (selectedCourse?.id === course.id) {
      setSelectedCourse(null);
    } else {
      setSelectedCourse(course);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewCourseStudents = (course) => {
    handleViewCourseDetails(course);
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewCourseTopics = (course) => {
    handleViewCourseDetails(course);
  };

  const handleEditCourse = (course) => {
    // Navigate to course management or implement inline editing
    navigate('/admin/courses');
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await coursesService.deleteCourse(courseId);
        alert('Course deleted successfully!');
      } catch (error) {
        console.error('Error deleting course:', error);
        alert('Error deleting course. Please try again.');
      }
    }
  };

  const handleDeactivateCourse = async (course) => {
    if (window.confirm('Are you sure you want to deactivate this course? This will hide it from students but preserve all data.')) {
      try {
        await coursesService.updateCourse(course.id, { isActive: false });
        alert('Course deactivated successfully!');
      } catch (error) {
        console.error('Error deactivating course:', error);
        alert('Error deactivating course. Please try again.');
      }
    }
  };


  const toggleCourseExpansion = (courseId) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const toggleSessionExpansion = (sessionId) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleToggleStudentClassroom = async (sessionId, studentId, enabled) => {
    try {
      const registration = registrations[sessionId]?.find(reg => reg.studentId === studentId);
      if (registration) {
        await sessionRegistrationsService.enableClassroomForStudent(sessionId, registration.id, enabled);
        alert(`Classroom access ${enabled ? 'enabled' : 'disabled'} for ${getStudentDisplayName(studentsMap[studentId]) || 'student'}!`);
        
        // Refresh registrations
        const updatedRegistrations = await sessionRegistrationsService.getRegistrationsForSession(sessionId);
        setRegistrations(prev => ({ ...prev, [sessionId]: updatedRegistrations }));
      }
    } catch (error) {
      console.error('Error toggling classroom access:', error);
      alert('Error updating classroom access. Please try again.');
    }
  };

  const handleUpdateClassroomUrl = async (sessionId, url) => {
    try {
      await sessionsService.updateSession(sessionId, { classroomUrl: url });
      alert(`Video conference URL ${url ? 'updated' : 'removed'} successfully!`);
      
      // Sessions will be automatically refreshed by the useData hook
    } catch (error) {
      console.error('Error updating classroom URL:', error);
      alert('Error updating classroom URL. Please try again.');
    }
  };

  const handleActivateInAppClassroom = async (sessionId) => {
    try {
      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        alert('Session not found!');
        return;
      }

      // Enable classroom for all active students in this session
      const sessionRegistrations = registrations[sessionId] || [];
      const activeRegistrations = sessionRegistrations.filter(reg => reg.status === 'active');
      
      if (activeRegistrations.length === 0) {
        alert('No active students assigned to this session. Please assign students first!');
        return;
      }

      // Enable classroom for all active students
      await sessionRegistrationsService.enableClassroomForAllStudents(sessionId, true);
      
      alert(`🚀 In-App Classroom ACTIVATED for ${activeRegistrations.length} students!\n\nStudents can now:\n• Access interactive components\n• Work on quizzes and activities\n• Collaborate in real-time\n\nGo to Student Dashboard to see the classroom!`);
      
      // Refresh registrations
      const updatedRegistrations = await sessionRegistrationsService.getSessionRegistrations(sessionId);
      setRegistrations(prev => ({ ...prev, [sessionId]: updatedRegistrations }));
    } catch (error) {
      console.error('Error activating in-app classroom:', error);
      alert('Error activating classroom. Please try again.');
    }
  };

  const getStudentDisplayName = (student) => {
    if (!student) return '';
    const combined = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
    return student.name || student.displayName || student.fullName || combined || student.email || '';
  };

  const getCoachDisplayName = (coachId) => {
    if (!coachId) return '';
    const coach = coachMap[coachId];
    if (!coach) return '';
    const combined = [coach.firstName, coach.lastName].filter(Boolean).join(' ').trim();
    return coach.name || coach.displayName || coach.fullName || combined || '';
  };

  const buildReminderPayload = (session, course, student) => ({
    studentEmail: student.email,
    studentName: getStudentDisplayName(student),
    courseTitle: course?.title || session.title || 'Live Learning Session',
    sessionTitle: session.title || course?.title || 'Live Session',
    sessionSchedule: formatDateDisplay(session.startDate),
    sessionStartDate: session.startDate,
    sessionEndDate: session.endDate,
    classroomUrl: session.classroomUrl,
    coachName: getCoachDisplayName(session.coachId),
    sessionId: session.id
  });

  const showEmailStatus = (type, message) => {
    setEmailFeedback({ type, message });
  };

  const handleSendReminderToStudent = async (session, course, registration) => {
    const student = studentsMap[registration.studentId];
    if (!student?.email) {
      showEmailStatus('error', 'Student email is missing for this record.');
      return;
    }

    const registrationKey = registration.id || `${session.id}_${registration.studentId}`;
    if (studentEmailSending[registrationKey]) {
      return;
    }

    setStudentEmailSending(prev => ({ ...prev, [registrationKey]: true }));
    try {
      const payload = buildReminderPayload(session, course, student);
      await emailService.sendSessionReminderEmail(payload);
      const displayName = getStudentDisplayName(student) || student.email;
      showEmailStatus('success', `Reminder queued for ${displayName}.`);
    } catch (error) {
      console.error('Error sending student reminder email:', error);
      showEmailStatus('error', 'Unable to send reminder. Please try again.');
    } finally {
      setStudentEmailSending(prev => {
        const next = { ...prev };
        delete next[registrationKey];
        return next;
      });
    }
  };

  const handleSendRemindersToSession = async (session, course) => {
    const sessionRegistrations = registrations[session.id] || [];
    const targetRegistrations = sessionRegistrations.filter(reg => ['active', 'pending'].includes(reg.status));
    const payloads = targetRegistrations
      .map(reg => {
        const student = studentsMap[reg.studentId];
        if (!student?.email) {
          return null;
        }
        return buildReminderPayload(session, course, student);
      })
      .filter(Boolean);

    if (payloads.length === 0) {
      showEmailStatus('error', 'No student emails available for this session.');
      return;
    }

    if (sessionEmailSending[session.id]) {
      return;
    }

    setSessionEmailSending(prev => ({ ...prev, [session.id]: true }));
    try {
      await emailService.sendBulkSessionReminderEmails(payloads);
      showEmailStatus('success', `Reminder emails queued for ${payloads.length} student${payloads.length === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Error sending session reminders:', error);
      showEmailStatus('error', 'Failed to queue reminders for this session.');
    } finally {
      setSessionEmailSending(prev => {
        const next = { ...prev };
        delete next[session.id];
        return next;
      });
    }
  };

  const buildTestReminderPayload = () => ({
    studentEmail: (testEmail && testEmail.trim()) || 'student@example.com',
    studentName: 'Test Recipient',
    courseTitle: 'Test Reminder Course',
    sessionTitle: 'Test Session Reminder',
    sessionSchedule: new Date().toLocaleString(),
    sessionStartDate: new Date().toISOString(),
    sessionEndDate: new Date(Date.now() + 3600000).toISOString(),
    classroomUrl: window.location.origin,
    coachName: user?.displayName || 'Learn for Fun Team',
    sessionId: 'test-session'
  });

  const handleSendTestReminder = async () => {
    const trimmed = testEmail.trim();
    if (!trimmed) {
      showEmailStatus('error', 'Enter a test email address first.');
      return;
    }

    if (testEmailSending) return;

    setTestEmailSending(true);
    try {
      const payload = buildTestReminderPayload();
      await emailService.sendSessionReminderEmail({ ...payload, studentEmail: trimmed });
      showEmailStatus('success', `Test reminder queued for ${trimmed}.`);
    } catch (error) {
      console.error('Error sending test reminder:', error);
      showEmailStatus('error', 'Unable to queue test reminder.');
    } finally {
      setTestEmailSending(false);
    }
  };

  const handlePreviewTestReminder = () => {
    const payload = buildTestReminderPayload();
    try {
      const html = emailService.generateSessionReminderHTML(payload);
      setPreviewHtml(html);
      setShowEmailPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      showEmailStatus('error', 'Unable to render email preview.');
    }
  };

  return (
    <div className="sessions-management">
      <div className="page-header">
        <h1>📅 Sessions Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {user && user.type === 'admin' && (
            <div className="page-views" style={{ fontSize: '1rem', color: '#666' }}>
              👁️ {pageViews.toLocaleString()} views
            </div>
          )}
          <button className="add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
            ➕ Add New Session
          </button>
        </div>
      </div>

      {emailFeedback && (
        <div className={`status-banner ${emailFeedback.type}`}>
          <span>{emailFeedback.message}</span>
          <button type="button" onClick={() => setEmailFeedback(null)} aria-label="Dismiss notification">
            ✕
          </button>
        </div>
      )}

      <div className="test-email-panel">
        <div>
          <h3>✉️ Send Test Reminder</h3>
          <p>Instantly queue a reminder email to verify template rendering.</p>
        </div>
        <div className="test-email-form">
          <input
            type="email"
            placeholder="test@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
          />
          <button type="button" onClick={handleSendTestReminder} disabled={testEmailSending}>
            {testEmailSending ? 'Sending...' : 'Send Test Email'}
          </button>
          <button type="button" className="outline" onClick={handlePreviewTestReminder}>
            Preview Email
          </button>
        </div>
      </div>


      {showEmailPreview && (
        <div className="preview-modal-overlay" onClick={() => setShowEmailPreview(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>📧 Email Preview</h3>
              <div className="preview-modal-actions">
                <button 
                  type="button" 
                  className="preview-close-btn"
                  onClick={() => setShowEmailPreview(false)}
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="preview-modal-body">
              <iframe 
                title="Email Preview" 
                srcDoc={previewHtml} 
                frameBorder="0"
                className="email-preview-iframe"
              />
            </div>
            <div className="preview-modal-footer">
              <p className="preview-note">
                💡 This is how the email will appear to recipients. The email will be sent via the email queue system.
              </p>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingSession ? 'Edit Session' : 'Create New Session'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Session Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Total Duration (hours) *</label>
                <input
                  type="number"
                  value={formData.totalDuration}
                  onChange={(e) => setFormData({ ...formData, totalDuration: e.target.value })}
                  min="0"
                  step="0.5"
                  required
                />
              </div>
              <div className="form-group">
                <label>Course</label>
                <select
                  value={formData.courseId}
                  onChange={(e) => handleCourseChange(e.target.value)}
                >
                  <option value="">Select a course (optional)</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.title}</option>
                  ))}
                </select>
              </div>
              
              {formData.courseId && (() => {
                const selectedCourse = courses.find(c => c.id === formData.courseId);
                return selectedCourse && selectedCourse.topics && selectedCourse.topics.length > 0 ? (
                  <div className="form-group topic-dates-section">
                    <label>Topic Dates (Select dates for each topic)</label>
                    {selectedCourse.topics.map((topic, idx) => (
                      <div key={idx} className="topic-date-group">
                        <div className="topic-date-header">
                          <strong>{topic.name}</strong>
                          <button
                            type="button"
                            onClick={() => handleAddTopicDate(topic.name)}
                            className="add-date-btn-small"
                          >
                            ➕ Add Date
                          </button>
                        </div>
                        {(formData.topicDates[topic.name] || []).map((date, dateIdx) => (
                          <div key={dateIdx} className="date-input-row">
                            <input
                              type="date"
                              value={date}
                              onChange={(e) => handleTopicDateChange(topic.name, dateIdx, e.target.value)}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveTopicDate(topic.name, dateIdx)}
                              className="remove-date-btn"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                        {(!formData.topicDates[topic.name] || formData.topicDates[topic.name].length === 0) && (
                          <p className="no-dates-hint">No dates added yet. Click "Add Date" to add dates for this topic.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              <div className="form-group">
                <label>Course Objectives</label>
                <textarea
                  value={formData.courseObjects}
                  onChange={(e) => setFormData({ ...formData, courseObjects: e.target.value })}
                  rows="3"
                  placeholder="Enter course objectives..."
                />
              </div>
              <div className="form-group">
                <label>Expectations</label>
                <textarea
                  value={formData.expectations}
                  onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                  rows="3"
                  placeholder="Enter expectations for students..."
                />
              </div>
              <div className="form-group">
                <label>Coach</label>
                <select
                  value={formData.coachId}
                  onChange={(e) => setFormData({ ...formData, coachId: e.target.value })}
                >
                  <option value="">Select a coach (optional)</option>
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.id}>{coach.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>💰 Session Cost ($)</label>
                <input
                  type="number"
                  value={formData.sessionCost}
                  onChange={(e) => setFormData({ ...formData, sessionCost: e.target.value })}
                  placeholder="e.g. 400"
                  min="0"
                  step="1"
                />
              </div>
              <div className="form-group">
                <label>📅 Cost Schedule / Payment Terms</label>
                <input
                  type="text"
                  value={formData.costSchedule}
                  onChange={(e) => setFormData({ ...formData, costSchedule: e.target.value })}
                  placeholder="e.g. 50% at the beginning, 50% after 4th week"
                />
              </div>
              <div className="form-group">
                <label>Fees/Terms (full text for display)</label>
                <textarea
                  value={formData.feesTerms}
                  onChange={(e) => setFormData({ ...formData, feesTerms: e.target.value })}
                  rows="3"
                  placeholder="Enter fees and terms information (e.g., $400 total. 50% at the beginning, 50% after 4th week.)"
                />
              </div>
                            <div className="form-group">
                <label>👥 Maximum Students (Optional)</label>
                <input
                  type="number"
                  value={formData.maxStudents}
                  onChange={(e) => setFormData({ ...formData, maxStudents: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
                <small className="form-hint">
                  Set maximum number of students allowed in this session. Leave empty for unlimited.
                </small>
              </div>
              <div className="form-group">
                <SessionFeatureToggles
                  title="Interactive Experience Controls"
                  subtitle="Toggle which classroom tabs and interactive modules are enabled when this session goes live."
                  toggles={formData.featureToggles}
                  onToggleChange={handleFormFeatureToggleChange}
                  variant="form"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={saving}>
                  {saving ? '💾 Saving...' : '💾 Save'}
                </button>
                <button type="button" onClick={resetForm} className="cancel-btn" disabled={saving}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="courses-management">
        {loading ? (
          <div className="empty-state">
            <p>Loading courses...</p>
          </div>
        ) : courses.length === 0 ? (
          <div className="empty-state">
            <p>No courses yet. Create your first course!</p>
          </div>
        ) : (
          courses.map((course) => {
            // Get sessions for this course
            const courseSessions = sessions.filter(s => s.courseId === course.id);
            const totalStudents = courseSessions.reduce((sum, session) => sum + (registrations[session.id]?.length || 0), 0);
            const aggregatedCourseStats = courseSessions.reduce(
              (acc, session) => {
                const stats = deriveSessionRegistrationStats(session.id);
                acc.attempted += stats.attempted || 0;
                acc.completed += stats.completed || 0;
                return acc;
              },
              { attempted: 0, completed: 0 }
            );
            
            const isExpanded = expandedCourses.has(course.id);
            const hasTopics = course.topics && course.topics.length > 0;
            
            return (
            <div key={course.id} className="course-row">
              {/* Course Row Header - Always Visible */}
              <div className="course-row-header" onClick={() => hasTopics && toggleCourseExpansion(course.id)}>
                <div className="course-row-info">
                  <div className="course-expand-icon">
                    {hasTopics ? (isExpanded ? '📂' : '📁') : '📄'}
                  </div>
                  <div className="course-basic-info">
                    <h3 className="course-title">{course.title}</h3>
                    <div className="course-meta">
                      <span className="meta-item">{courseSessions.length} sessions</span>
                      <span className="meta-item">{totalStudents} students</span>
                      <span className="meta-item">{hasTopics ? `${course.topics.length} topics` : 'No topics'}</span>
                      <span className="meta-item status">{course.isActive ? '🟢 Active' : '🔴 Inactive'}</span>
                    </div>
                  </div>
                </div>
                <div className="course-row-actions" onClick={(e) => e.stopPropagation()}>
                  {totalStudents === 0 ? (
                    <button onClick={() => handleDeleteCourse(course.id)} className="delete-btn" title="Delete course">🗑️</button>
                  ) : (
                    <button onClick={() => handleDeactivateCourse(course)} className="deactivate-btn" title="Deactivate course">🔒</button>
                  )}
                  <button onClick={() => handleEditCourse(course)} className="edit-btn" title="Edit course">✏️</button>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && hasTopics && (
                <div className="course-expanded-content">
                  {/* Course Details */}
                  <div className="course-details-expanded">
                    <div className="details-grid">
                      <div className="detail-card">
                        <h4>📋 Course Info</h4>
                        <p><strong>Description:</strong> {course.description || 'No description'}</p>
                        <p><strong>Duration:</strong> {
                          course.duration || 
                          (course.topics && course.topics.length > 0 
                            ? `${course.topics.reduce((sum, topic) => sum + (parseFloat(topic.hours) || 0), 0)} hours (total topics)`
                            : 'Not specified'
                          )
                        }</p>
                      </div>
                    </div>
                  </div>

                  <div className="course-sessions-section">
                    <div className="course-sessions-header">
                      <h4>📋 Active Sessions - Live Classroom</h4>
                      <button
                        className="add-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetForm();
                          setFormData(prev => ({
                            ...prev,
                            courseId: course.id
                          }));
                          setShowForm(true);
                        }}
                      >
                        ➕ Create Session
                      </button>
                    </div>

                    {courseSessions.length === 0 ? (
                      <div className="empty-state compact">
                        <p>No sessions created for this course yet.</p>
                      </div>
                    ) : (
                      <div className="sessions-list nested">
                        {courseSessions.map((session, sessionIdx) => {
                          const sessionRegistrations = registrations[session.id] || [];
                          const activeRegistrations = sessionRegistrations.filter((reg) => reg.status === 'active');
                          const pendingRegistrations = sessionRegistrations.filter((reg) => reg.status === 'pending');
                          const stats = deriveSessionRegistrationStats(session.id);
                          const sessionViews = sessionViewStatsMap[session.id] || { viewCount: 0, uniqueViewers: 0 };
                          const sessionNumber = getSessionNumber(sessionIdx);
                          const sessionTitleWithNumber = `Session ${sessionNumber}: ${session.title || 'Untitled Session'}`;
                          const isSessionExpanded = expandedSessions.has(session.id);

                          const sessionMeta = buildSessionMeta(session, {
                            courseTitle: course.title,
                            totalStudents: sessionRegistrations.length,
                            activeStudents: activeRegistrations.length,
                            pendingStudents: pendingRegistrations.length,
                            registrationStats: stats,
                            classroomReady: Boolean(session.classroomUrl),
                            viewStats: sessionViews
                          });

                          const actions = (
                            <div className="admin-session-actions">
                              <button
                                className="open-live-classroom-btn"
                                onClick={() => navigate(`/admin/classroom/${session.id}`)}
                              >
                                💻 Open Live Classroom
                              </button>
                              <button
                                className="assign-coach-btn"
                                onClick={() => handleOpenCoachAssignment(session)}
                              >
                                {session.coachId ? '👤 Change Coach' : '👤 Assign Coach'}
                              </button>
                              <button
                                className={`classroom-url-btn ${session.classroomUrl ? 'ready' : 'pending'}`}
                                onClick={() => {
                                  const url = prompt('Enter video conference URL (Zoom, Google Meet, etc.):', session.classroomUrl || '');
                                  if (url !== null) {
                                    handleUpdateClassroomUrl(session.id, url);
                                  }
                                }}
                              >
                                📹 {session.classroomUrl ? 'Update Video URL' : 'Set Video URL'}
                              </button>
                              {session.classroomUrl && (
                                <button
                                  className="join-classroom-btn"
                                  onClick={() => window.open(session.classroomUrl, '_blank')}
                                >
                                  🎥 Join Video Call
                                </button>
                              )}
                              <button
                                className="activate-classroom-btn"
                                onClick={() => handleActivateInAppClassroom(session.id)}
                              >
                                🚀 Activate In-App Classroom
                              </button>
                              <button onClick={() => setShowAssignStudent(session.id)} className="assign-student-btn">
                                👥 Assign Students
                              </button>
                              <button
                                className="reminder-btn"
                                disabled={Boolean(sessionEmailSending[session.id])}
                                onClick={() => handleSendRemindersToSession(session, course)}
                              >
                                {sessionEmailSending[session.id] ? '⏳ Sending Reminders...' : '✉️ Email All Students'}
                              </button>
                              <button onClick={() => setShowAttendance(session.id)} className="attendance-btn">
                                📊 Mark Attendance
                              </button>
                              <button onClick={() => handleViewHomeworkSubmissions(session.id)} className="homework-submissions-btn">
                                📝 Homework Submissions
                              </button>
                              <button onClick={() => handleEdit(session)} className="edit-btn">
                                ✏️ Edit
                              </button>
                              <button onClick={() => handleDelete(session.id)} className="delete-btn">
                                🗑️ Delete
                              </button>
                            </div>
                          );

                          const secondaryStatus = session.classroomUrl
                            ? { label: 'Classroom Ready', type: 'active' }
                            : { label: 'Classroom Not Set', type: 'pending' };

                          return (
                            <div key={session.id} className="session-wrapper">
                              <div 
                                className="session-header-collapsible"
                                onClick={() => toggleSessionExpansion(session.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleSessionExpansion(session.id);
                                  }
                                }}
                              >
                                <div className="session-header-content">
                                  <span className="session-expand-icon">{isSessionExpanded ? '▼' : '▶'}</span>
                                  <h4 className="session-header-title">{sessionTitleWithNumber}</h4>
                                  <span className={`status-badge ${getSessionStatus(session)?.type || 'default'}`}>
                                    {getSessionStatus(session)?.label || 'Active'}
                                  </span>
                                  {secondaryStatus && (
                                    <span className={`status-badge secondary ${secondaryStatus.type || 'info'}`}>
                                      {secondaryStatus.label}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isSessionExpanded && (
                                <div className="session-card-expanded">
                                  <SessionCard
                                    className="admin-session-card"
                                    title={sessionTitleWithNumber}
                                    status={getSessionStatus(session)}
                                    secondaryStatus={secondaryStatus}
                                    meta={sessionMeta}
                                    course={buildCourseSection(session, course)}
                                    topics={buildSessionTopics(session, course)}
                                    actions={actions}
                                    defaultCollapsed={true}
                                  >
                                    {/* Session Stats */}
                                    <div className="session-stats-card">
                                <h4>📊 Session Statistics</h4>
                                <div className="session-stat-line">
                                  <span className="stat-item">
                                    <strong>{stats.attempted || 0}</strong> attempted
                                  </span>
                                  <span className="stat-item">
                                    <strong>{stats.completed || 0}</strong> completed
                                  </span>
                                  <span className="stat-item">
                                    <strong>{sessionViews.viewCount || 0}</strong> views
                                  </span>
                                  <span className="stat-item">
                                    <strong>{sessionViews.uniqueViewers || 0}</strong> unique
                                  </span>
                                </div>
                              </div>

                              {/* Students List for this Session */}
                              {sessionRegistrations.length > 0 && (
                                <div className="session-students-section">
                                  <h4>👥 Enrolled Students ({sessionRegistrations.length})</h4>
                                  <div className="students-grid">
                                    {sessionRegistrations.map(reg => {
                                      const student = studentsMap[reg.studentId];
                                      if (!student) return null;
                                      const registrationKey = reg.id || `${session.id}_${reg.studentId}`;
                                      return (
                                        <div key={reg.id} className="student-card">
                                          <div className="student-header">
                                            <strong>{getStudentDisplayName(student)}</strong>
                                            <span className="student-email">{student.email}</span>
                                          </div>
                                          <div className="student-actions">
                                            <button
                                              className="email-btn small"
                                              onClick={() => handleSendReminderToStudent(session, course, reg)}
                                              disabled={Boolean(studentEmailSending[registrationKey])}
                                              title="Send reminder email"
                                            >
                                              {studentEmailSending[registrationKey] ? '⏳' : '✉️'}
                                            </button>
                                            <button 
                                              className={`classroom-toggle-btn small ${reg.classroomEnabled ? 'enabled' : 'disabled'}`}
                                              onClick={() => handleToggleStudentClassroom(session.id, reg.studentId, !reg.classroomEnabled)}
                                            >
                                              🎓
                                            </button>
                                            <button 
                                              className="rewards-btn small"
                                              onClick={() => {
                                                setSelectedStudent(student);
                                                setShowRewardsModal(true);
                                              }}
                                              title="Rewards"
                                            >
                                              🏆
                                            </button>
                                            <button 
                                              className="homework-btn small"
                                              onClick={() => {
                                                setSelectedStudent(student);
                                                setShowHomeworkModal(true);
                                              }}
                                              title="Homework"
                                            >
                                              📝
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                                    {renderSessionFeatureToggles(session)}
                                  </SessionCard>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
            );
          })
        )}
      </div>

      {unassignedSessions.length > 0 && (
        <section className="sessions-section unassigned">
          <div className="sessions-section-header">
            <h2>📋 Active Sessions - Live Classroom</h2>
            <button
              className="add-btn"
              onClick={() => {
                resetForm();
                setFormData(prev => ({
                  ...prev,
                  courseId: ''
                }));
                setShowForm(true);
              }}
            >
              🎓 Create New Session
            </button>
          </div>

          <div className="sessions-list">
            {unassignedSessions.map((session) => {
              const sessionRegistrations = registrations[session.id] || [];
              const activeRegistrations = sessionRegistrations.filter((reg) => reg.status === 'active');
              const pendingRegistrations = sessionRegistrations.filter((reg) => reg.status === 'pending');
              const isSessionExpanded = expandedSessions.has(session.id);

              const sessionMeta = buildSessionMeta(session, {
                courseTitle: 'No course assigned',
                totalStudents: sessionRegistrations.length,
                activeStudents: activeRegistrations.length,
                pendingStudents: pendingRegistrations.length,
                registrationStats: deriveSessionRegistrationStats(session.id),
                classroomReady: Boolean(session.classroomUrl),
                viewStats: sessionViewStatsMap[session.id]
              });

              const actions = (
                <div className="admin-session-actions">
                  <button
                    className="open-live-classroom-btn"
                    onClick={() => navigate(`/admin/classroom/${session.id}`)}
                  >
                    💻 Open Live Classroom
                  </button>
                  <button
                    className="assign-coach-btn"
                    onClick={() => handleOpenCoachAssignment(session)}
                  >
                    {session.coachId ? '👤 Change Coach' : '👤 Assign Coach'}
                  </button>
                  <button
                    className={`classroom-url-btn ${session.classroomUrl ? 'ready' : 'pending'}`}
                    onClick={() => {
                      const url = prompt('Enter video conference URL (Zoom, Google Meet, etc.):', session.classroomUrl || '');
                      if (url !== null) {
                        handleUpdateClassroomUrl(session.id, url);
                      }
                    }}
                  >
                    📹 {session.classroomUrl ? 'Update Video URL' : 'Set Video URL'}
                  </button>
                  {session.classroomUrl && (
                    <button
                      className="join-classroom-btn"
                      onClick={() => window.open(session.classroomUrl, '_blank')}
                    >
                      🎥 Join Video Call
                    </button>
                  )}
                  <button
                    className="activate-classroom-btn"
                    onClick={() => handleActivateInAppClassroom(session.id)}
                  >
                    🚀 Activate In-App Classroom
                  </button>
                  <button onClick={() => setShowAssignStudent(session.id)} className="assign-student-btn">
                    👥 Assign Students
                  </button>
                  <button
                    className="reminder-btn"
                    disabled={Boolean(sessionEmailSending[session.id])}
                    onClick={() => handleSendRemindersToSession(session, null)}
                  >
                    {sessionEmailSending[session.id] ? '⏳ Sending Reminders...' : '✉️ Email All Students'}
                  </button>
                  <button onClick={() => setShowAttendance(session.id)} className="attendance-btn">
                    📊 Mark Attendance
                  </button>
                  <button onClick={() => handleEdit(session)} className="edit-btn">
                    ✏️ Edit
                  </button>
                  <button onClick={() => handleDelete(session.id)} className="delete-btn">
                    🗑️ Delete
                  </button>
                </div>
              );

              const secondaryStatus = session.classroomUrl
                ? { label: 'Classroom Ready', type: 'active' }
                : { label: 'Classroom Not Set', type: 'pending' };

              return (
                <div key={session.id} className="session-wrapper">
                  <div 
                    className="session-header-collapsible"
                    onClick={() => toggleSessionExpansion(session.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSessionExpansion(session.id);
                      }
                    }}
                  >
                    <div className="session-header-content">
                      <span className="session-expand-icon">{isSessionExpanded ? '▼' : '▶'}</span>
                      <h4 className="session-header-title">{session.title}</h4>
                      <span className={`status-badge ${getSessionStatus(session)?.type || 'default'}`}>
                        {getSessionStatus(session)?.label || 'Active'}
                      </span>
                      {secondaryStatus && (
                        <span className={`status-badge secondary ${secondaryStatus.type || 'info'}`}>
                          {secondaryStatus.label}
                        </span>
                      )}
                    </div>
                  </div>
                  {isSessionExpanded && (
                    <div className="session-card-expanded">
                      <SessionCard
                        className="admin-session-card"
                        title={session.title}
                        status={getSessionStatus(session)}
                        secondaryStatus={secondaryStatus}
                        meta={sessionMeta}
                        course={buildCourseSection(session)}
                        topics={buildSessionTopics(session, session.course)}
                        actions={actions}
                        defaultCollapsed={true}
                      >
                        {renderSessionFeatureToggles(session)}
                      </SessionCard>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}


      
      {showAssignStudent && (
        <div className="modal-overlay" onClick={() => setShowAssignStudent(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Assign Student to Session</h2>
            <div className="assign-student-form">
              <p>Select a student to assign to this session:</p>
              <div className="students-list-select">
                {Object.values(studentsMap).filter(s => s.type === 'student').length === 0 ? (
                  <p>No students available.</p>
                ) : (
                  Object.values(studentsMap)
                    .filter(s => s.type === 'student')
                    .map((student) => {
                      const isAlreadyRegistered = registrations[showAssignStudent]?.some(
                        reg => reg.studentId === student.id
                      );
                      return (
                        <div key={student.id} className="student-select-item">
                          <div className="student-info-select">
                            <strong>{getStudentDisplayName(student)}</strong>
                            <span className="student-email-select">{student.email}</span>
                          </div>
                          <button
                            onClick={() => handleAssignStudentToSession(showAssignStudent, student.id)}
                            disabled={isAlreadyRegistered}
                            className={`assign-student-to-session-btn ${isAlreadyRegistered ? 'disabled' : ''}`}
                          >
                            {isAlreadyRegistered ? '✅ Already Assigned' : '➕ Assign'}
                          </button>
                        </div>
                      );
                    })
                )}
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAssignStudent(null)} className="cancel-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAssignCoach && (() => {
        const targetSession = sessions.find((s) => s.id === showAssignCoach);
        return (
          <div className="modal-overlay" onClick={closeCoachModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Assign Coach</h2>
              <p className="modal-subtitle">
                Session: <strong>{targetSession?.title || 'Untitled Session'}</strong>
              </p>
              <div className="form-group">
                <label>Select Coach</label>
                <select
                  value={selectedCoachId}
                  onChange={(e) => setSelectedCoachId(e.target.value)}
                >
                  <option value="">No Coach Assigned</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {getCoachDisplayName(coach.id) || coach.email || 'Unnamed Coach'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button
                  className="save-btn"
                  onClick={handleAssignCoachToSession}
                  disabled={assigningCoach}
                >
                  {assigningCoach ? 'Saving...' : 'Save Coach'}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeCoachModal}
                  disabled={assigningCoach}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showAttendance && (() => {
        const session = sessions.find(s => s.id === showAttendance);
        if (!session || !session.courseId) return null;
        const course = courses.find(c => c.id === session.courseId);
        const activeRegistrations = registrations[showAttendance]?.filter(r => r.status === 'active') || [];
        
        return (
          <div className="modal-overlay" onClick={() => setShowAttendance(null)}>
            <div className="modal-content attendance-modal" onClick={(e) => e.stopPropagation()}>
              <h2>📊 Mark Attendance - {session.title}</h2>
              {course && course.topics && course.topics.length > 0 ? (
                <div className="attendance-topics">
                  {course.topics.map((topic, idx) => {
                    const topicDates = session.topicDates?.[topic.name] || [];
                    return (
                      <div key={idx} className="attendance-topic-section">
                        <h3>{topic.name}</h3>
                        {topicDates.length > 0 ? (
                          <div className="attendance-dates-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Student</th>
                                  {topicDates.map((date, dateIdx) => {
                                    const dateStr = typeof date === 'string' ? date : (date?.toDate?.()?.toISOString() || date);
                                    return (
                                      <th key={dateIdx}>
                                        {new Date(dateStr).toLocaleDateString()}
                                      </th>
                                    );
                                  })}
                                </tr>
                              </thead>
                              <tbody>
                                {activeRegistrations.map((reg) => {
                                  const student = studentsMap[reg.studentId];
                                  if (!student) return null;
                                  return (
                                    <tr key={reg.id}>
                                      <td><strong>{getStudentDisplayName(student)}</strong></td>
                                      {topicDates.map((date, dateIdx) => {
                                        const dateStr = typeof date === 'string' ? date : (date?.toDate?.()?.toISOString() || date);
                                        const key = `${reg.studentId}_${topic.name}_${dateStr}`;
                                        const att = attendanceData[key];
                                        const status = att?.status || 'not-marked';
                                        return (
                                          <td key={dateIdx}>
                                            <select
                                              value={status}
                                              onChange={(e) => handleMarkAttendance(showAttendance, reg.studentId, topic.name, dateStr, e.target.value)}
                                              className={`attendance-select ${status}`}
                                            >
                                              <option value="not-marked">-</option>
                                              <option value="attended">✅ Attended</option>
                                              <option value="not-attended">❌ Not Attended</option>
                                              <option value="absent">⚠️ Absent</option>
                                            </select>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="no-dates-msg">No dates assigned for this topic. Edit session to add dates.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No course or topics assigned to this session.</p>
              )}
              <div className="form-actions">
                <button type="button" onClick={() => setShowAttendance(null)} className="cancel-btn">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Session Component Configuration Modal */}

      {/* Rewards Modal */}
      {showRewardsModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowRewardsModal(false)}>
          <div className="modal-content rewards-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🏆 Student Rewards - {getStudentDisplayName(selectedStudent)}</h2>
            <div className="rewards-content">
              <div className="reward-item">
                <h3>🌟 Attendance Bonus</h3>
                <p>Perfect attendance for this session</p>
                <button className="award-reward-btn">Award 50 Points</button>
              </div>
              <div className="reward-item">
                <h3>💡 Participation Award</h3>
                <p>Active participation in classroom activities</p>
                <button className="award-reward-btn">Award 30 Points</button>
              </div>
              <div className="reward-item">
                <h3>📚 Homework Excellence</h3>
                <p>Completed all homework assignments</p>
                <button className="award-reward-btn">Award 40 Points</button>
              </div>
              <div className="reward-item">
                <h3>🎯 Topic Master</h3>
                <p>Excellence in specific topics</p>
                <button className="award-reward-btn">Award 25 Points</button>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowRewardsModal(false)} className="cancel-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Homework Modal */}
      {showHomeworkModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowHomeworkModal(false)}>
          <div className="modal-content homework-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📝 Homework Management - {getStudentDisplayName(selectedStudent)}</h2>
            <div className="homework-content">
              <div className="homework-section">
                <h3>📚 Current Assignments</h3>
                <div className="assignment-item">
                  <h4>JavaScript Fundamentals</h4>
                  <p>Complete exercises 1-10</p>
                  <span className="status pending">⏳ Pending</span>
                  <button className="mark-complete-btn">Mark Complete</button>
                </div>
                <div className="assignment-item">
                  <h4>React Components</h4>
                  <p>Build a todo app component</p>
                  <span className="status completed">✅ Completed</span>
                  <button className="view-submission-btn">View Submission</button>
                </div>
              </div>
              <div className="homework-section">
                <h3>➕ Add New Assignment</h3>
                <div className="add-assignment-form">
                  <input type="text" placeholder="Assignment title" className="assignment-input" />
                  <textarea placeholder="Assignment description" className="assignment-textarea"></textarea>
                  <button className="add-assignment-btn">Add Assignment</button>
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowHomeworkModal(false)} className="cancel-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Homework Submissions Modal */}
      {showHomeworkSubmissions && (
        <div className="modal-overlay" onClick={() => setShowHomeworkSubmissions(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>📝 Homework Submissions</h2>
              <button 
                onClick={() => setShowHomeworkSubmissions(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {sessionHomeworks[showHomeworkSubmissions]?.length > 0 ? (
                <div>
                  {sessionHomeworks[showHomeworkSubmissions].map((homework) => {
                    const submissions = homeworkSubmissions[homework.id] || [];
                    return (
                      <div key={homework.id} style={{ marginBottom: '30px', padding: '20px', border: '2px solid #e2e8f0', borderRadius: '12px' }}>
                        <div style={{ marginBottom: '15px' }}>
                          <h3 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>{homework.title}</h3>
                          {homework.topicName && (
                            <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem' }}>
                              <strong>Topic:</strong> {homework.topicName}
                            </p>
                          )}
                          <p style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '0.9rem' }}>
                            <strong>Due:</strong> {new Date(homework.dueDate).toLocaleString()}
                          </p>
                          {homework.rewardPoints > 0 && (
                            <p style={{ margin: '0 0 8px 0', color: '#f59e0b', fontSize: '0.9rem', fontWeight: '600' }}>
                              ⭐ Reward Points: {homework.rewardPoints}
                            </p>
                          )}
                          {homework.description && (
                            <p style={{ margin: '8px 0', color: '#475569' }}>{homework.description}</p>
                          )}
                        </div>
                        <div>
                          <h4 style={{ margin: '0 0 12px 0', color: '#475569' }}>
                            Submissions ({submissions.length})
                          </h4>
                          {submissions.length > 0 ? (
                            <div>
                              {submissions.map((submission) => {
                                const student = studentsMap[submission.studentId];
                                const isAccepted = submission.status === 'accepted';
                                const isRejected = submission.status === 'rejected';
                                return (
                                  <div 
                                    key={submission.id} 
                                    style={{ 
                                      marginBottom: '15px', 
                                      padding: '15px', 
                                      backgroundColor: isAccepted ? '#f0fdf4' : isRejected ? '#fef2f2' : '#f8fafc',
                                      border: `2px solid ${isAccepted ? '#10b981' : isRejected ? '#ef4444' : '#e2e8f0'}`,
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                      <div>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>
                                          {student?.name || student?.email?.split('@')[0] || 'Unknown Student'}
                                        </strong>
                                        <small style={{ color: '#64748b' }}>
                                          Submitted: {new Date(submission.submittedAt).toLocaleString()}
                                        </small>
                                      </div>
                                      <span style={{
                                        padding: '4px 12px',
                                        borderRadius: '12px',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        backgroundColor: isAccepted ? '#10b981' : isRejected ? '#ef4444' : '#f59e0b',
                                        color: 'white'
                                      }}>
                                        {isAccepted ? '✅ Accepted' : isRejected ? '❌ Rejected' : '⏳ Pending'}
                                      </span>
                                    </div>
                                    {submission.answer && (
                                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Answer:</strong>
                                        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{submission.answer}</p>
                                      </div>
                                    )}
                                    {submission.files && submission.files.length > 0 && (
                                      <div style={{ marginBottom: '10px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Files:</strong>
                                        {submission.files.map((file, idx) => (
                                          <a 
                                            key={idx} 
                                            href={file.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                              display: 'inline-block', 
                                              marginRight: '8px', 
                                              padding: '4px 8px', 
                                              backgroundColor: '#e0e7ff', 
                                              borderRadius: '4px',
                                              textDecoration: 'none',
                                              color: '#4f46e5'
                                            }}
                                          >
                                            📎 {file.name}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                    {submission.feedback && (
                                      <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px' }}>
                                        <strong style={{ display: 'block', marginBottom: '6px' }}>Feedback:</strong>
                                        <p style={{ margin: 0 }}>{submission.feedback}</p>
                                      </div>
                                    )}
                                    {!isAccepted && !isRejected && (
                                      <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                                        <button
                                          onClick={() => handleAcceptHomework(homework.id, submission.id, homework.rewardPoints || 0)}
                                          style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          ✅ Accept
                                        </button>
                                        <button
                                          onClick={() => {
                                            const feedback = prompt('Enter rejection feedback (optional):');
                                            if (feedback !== null) {
                                              handleRejectHomework(homework.id, submission.id, feedback);
                                            }
                                          }}
                                          style={{
                                            padding: '8px 16px',
                                            border: 'none',
                                            borderRadius: '6px',
                                            backgroundColor: '#ef4444',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                          }}
                                        >
                                          ❌ Reject
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>No submissions yet.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
                  No homework assignments found for this session.
                </p>
              )}
            </div>
            <div className="form-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                onClick={() => setShowHomeworkSubmissions(null)} 
                className="cancel-btn"
                style={{ padding: '10px 20px', border: '2px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsManagement;

